import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Button, MenuItem, Popover } from "@blueprintjs/core"
import { ItemRendererProps, Select } from "@blueprintjs/select"
import { DatePicker3 } from "@blueprintjs/datetime2"

import { useDrag, useDrop } from "react-dnd"

import {
  AlarmClock,
  Bed,
  Bell,
  Check,
  Grip,
  MoreVertical,
  Trash2,
} from "lucide-react"
import { Location } from "@/reducers/locationsSlice"
import {
  Action,
  ACTIONS,
  findRouteConfig,
  RouteStop,
  stopActionLabel,
} from "@/reducers/routes2gSlice"
import LocationView from "@/components/routeStops/LocationView"
import {
  differenceInDays,
  format,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
} from "date-fns"
import { useDarkMode } from "@/contexts/DarkModeContext"
import css from "./RouteStopView.module.css"
import { isMobile } from "@/utils/detectDevice"
import RouteStopDetailsPopover from "@/components/RouteStopDetailPopover"
import { useMediaQuery } from "@react-hookz/web"
import { HoverPopover } from "@/components/ui/hover-popover"
import { Badge } from "@/components/ui/badge"
import {
  Popover as Popover2,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button as Button2 } from "@/components/ui/button"
import { AvoidType } from "@/api/adHocCommands"
import { DisplayedStopDistance } from "@/lib/stopDistance"
import {
  assumedRestLabelOf,
  stopArrivalOf,
  stopDistanceOf,
} from "@/lib/stopStatus"
import { useAppSelector } from "@/reducers/hooks"
import { DriverAvatar } from "@/components/analyzeOrderOnPage/DriverSelect"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import ChatHelpers from "@/lib/chatHelpers"
import { StopAutomationDialog } from "@/components/routeStops/StopAutomationDialog"
import {
  stopAvoidsTolls,
  withStopAction,
  withStopAvoidTolls,
  withStopLocation,
} from "@/lib/routeStopEdits"
import { useStopAutomation } from "@/components/routeStops/useStopAutomation"
import StationaryIndicator from "@/components/StationaryIndicator"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"
import { getDateFnsLocale } from "@/lib/i18n/format"

const DISABLED_COLOR = "var(--disabled-color, #5F6B7C)"
const DANGER_COLOR = "var(--danger-color, #CD4246)"
const LATE_COLOR = DANGER_COLOR

// The date-fns patterns live in the translation files because they hold words
// ("Today at 14:30", "Friday at 14:30", "Apr 15 at 14:30").
export const formatUserFriendlyDate = (date: Date): string => {
  const locale = getDateFnsLocale()
  const formatWith = (pattern: string) => format(date, pattern, { locale })

  if (isToday(date)) {
    return formatWith(i18n.t("maps.routeStopView.datePatterns.today"))
  }

  if (isTomorrow(date)) {
    return formatWith(i18n.t("maps.routeStopView.datePatterns.tomorrow"))
  }

  if (isYesterday(date)) {
    return formatWith(i18n.t("maps.routeStopView.datePatterns.yesterday"))
  }

  const daysDifference = differenceInDays(date, new Date())

  if (daysDifference > 0 && daysDifference < 7) {
    return formatWith(i18n.t("maps.routeStopView.datePatterns.weekday"))
  }

  if (isThisYear(date)) {
    return formatWith(i18n.t("maps.routeStopView.datePatterns.thisYear"))
  }

  return formatWith(i18n.t("maps.routeStopView.datePatterns.otherYear"))
}

export const formatDuration = (seconds: number): string => {
  seconds = Math.abs(seconds)
  if (seconds === 0) {
    return "0m"
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  const hoursStr = hours > 0 ? `${hours}h` : ""
  const minutesStr = minutes > 0 ? `${minutes}m` : ""
  const separator = hours > 0 && minutes > 0 ? " " : ""

  return (
    hoursStr + separator + minutesStr || i18n.t("maps.routeStopView.seconds")
  )
}

type ScheduleButtonProps = {
  stop: RouteStop
  disabled?: boolean
  onDateUpdate: (newDate: Date | null) => void
}
const ScheduleButton = ({
  stop,
  disabled,
  onDateUpdate,
}: ScheduleButtonProps) => {
  const { t } = useTranslation()
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [temporaryDate, setTemporaryDate] = useState<Date | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    setSelectedDate(
      stop.scheduled_arrival_time
        ? new Date(stop.scheduled_arrival_time)
        : null,
    )
  }, [stop])

  const handleDateChange = (newDate: Date | null): void => {
    setTemporaryDate(newDate)
  }

  const setDate = () => {
    setSelectedDate(temporaryDate)
    setIsOpen(false)
  }

  const handleClose = () => {
    onDateUpdate(selectedDate) // is this state always dispatched at this point?
    setIsOpen(false)
    window.analytics.track("ScheduleRouteStop", { id: stop.id })
  }

  const getIconColor = () => {
    if (stop.shouldAlert) return LATE_COLOR

    if (stop.isPending && stop.scheduled_arrival_time) return "#238551"

    return "#5f6b7c"
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      interactionKind="click"
      placement="auto"
      content={
        <div>
          <DatePicker3
            defaultValue={temporaryDate || undefined}
            onChange={handleDateChange}
            timePrecision="minute"
            timePickerProps={{ showArrowButtons: true }}
            showActionsBar={true}
            highlightCurrentDay={true}
            dayPickerProps={{ weekStartsOn: 1 }}
          />
          <Button intent="primary" className={css.setButton} onClick={setDate}>
            {t("common.save")}
          </Button>
        </div>
      }
      renderTarget={({ isOpen, ...targetProps }) => (
        <HoverPopover
          disabled={isMobile || isOpen}
          content={<RouteStopDetailsPopover routeStop={stop} />}
          side="bottom"
        >
          <Button
            id="stop-distance"
            {...targetProps}
            onClick={() => {
              setTemporaryDate(selectedDate)
              setIsOpen(!isOpen)
            }}
            minimal={true}
            disabled={disabled}
          >
            <AlarmClock size={16} color={getIconColor()} />
          </Button>
        </HoverPopover>
      )}
    />
  )
}

const AssumedRestMarker = ({ stop }: { stop: RouteStop }) => {
  const label = assumedRestLabelOf(stop)
  if (!label) return null
  return (
    <span aria-label={label} title={label} className="self-center">
      <Bed size={12} className="text-amber-600 dark:text-amber-400" />
    </span>
  )
}

const Delay = ({ stop }: { stop: RouteStop }) => {
  const { t } = useTranslation()
  const eta = stopArrivalOf(stop, Boolean(stop.is_active))
  if (!eta) return null

  const arrivalDateFormatted = format(new Date(eta.arrival), "HH:mm eee", {
    locale: getDateFnsLocale(),
  })

  if (eta.secondsLate === null) {
    return (
      <HoverPopover
        disabled={isMobile}
        content={<RouteStopDetailsPopover routeStop={stop} />}
        side="bottom"
      >
        <div
          className={
            "flex items-center gap-x-1 font-mono text-xs text-gray-800 dark:text-gray-300"
          }
        >
          {arrivalDateFormatted}
          <AssumedRestMarker stop={stop} />
        </div>
      </HoverPopover>
    )
  }

  const deltaSeconds = eta.secondsLate
  const delayDuration = formatDuration(deltaSeconds)

  return (
    <HoverPopover
      disabled={isMobile}
      content={<RouteStopDetailsPopover routeStop={stop} />}
      side="bottom"
    >
      <div className="flex flex-1 flex-row items-end gap-x-1 font-mono text-xs">
        <div
          className={
            deltaSeconds <= 0
              ? "self-center text-green-800 dark:text-green-500"
              : "self-center text-red-500"
          }
        >
          {arrivalDateFormatted}
        </div>
        <AssumedRestMarker stop={stop} />
        <div className="flex flex-1 flex-col items-end self-center pl-2 text-xs">
          {deltaSeconds > 0 && (
            <div className="text-red-500">
              {t("maps.routeStopView.delayed")}
            </div>
          )}
          {deltaSeconds > 0 && (
            <div className="text-[10px] text-red-500">({delayDuration})</div>
          )}
          {deltaSeconds <= 0 && (
            <div className="text-green-800 dark:text-green-500">
              {t("maps.routeStopView.onTime")}
            </div>
          )}
        </div>
      </div>
    </HoverPopover>
  )
}

type ActionDisplayProps = {
  stop: RouteStop
  disabled: boolean
  onActionUpdate: (action: string) => void
  isReadOnlyMap?: boolean
}

const ActionDisplay = ({
  stop,
  disabled,
  onActionUpdate,
  isReadOnlyMap = false,
}: ActionDisplayProps) => {
  const { t } = useTranslation()
  const driver = useAppSelector(findProfile(stop.by))

  const getActionByValue = (value: string | undefined): Action => {
    const action = ACTIONS.find(
      (action) => action.value.toLowerCase() === value,
    )
    return action ?? ACTIONS.find((action) => action.value === "other")!
  }

  const [selectedAction, setSelectedAction] = useState<Action>(
    getActionByValue(stop.action),
  )

  useEffect(() => {
    setSelectedAction(getActionByValue(stop.action))
  }, [stop])

  const handleItemSelect = (action: Action) => {
    setSelectedAction(action)
    onActionUpdate(action.value)
  }

  const renderAction = (
    option: Action,
    { handleClick, handleFocus, modifiers }: ItemRendererProps,
  ) => {
    return (
      <MenuItem
        data-testid={`action-${option.value}`}
        active={modifiers.active}
        key={option.value}
        icon={<option.icon className="mr-2" size={16} />}
        text={stopActionLabel(t, option.value) ?? option.label}
        onClick={handleClick}
        onFocus={handleFocus}
        roleStructure="listoption"
        selected={selectedAction?.value === option.value}
      />
    )
  }

  const getIconColor = (isDarkMode: boolean = false) => {
    if (disabled) return DISABLED_COLOR

    if (stop.shouldAlert) return LATE_COLOR

    return isDarkMode ? "#ffffff" : "#000000"
  }
  const { isDarkMode } = useDarkMode()
  if (stop.is_temporary && stop.action === "from_driver_location" && driver) {
    return (
      <div className="flex justify-center">
        <DriverAvatar driver={driver} size="xs" />
      </div>
    )
  }
  if (isReadOnlyMap) {
    return (
      <div className="flex justify-center px-2">
        <selectedAction.icon size={16} color={getIconColor(isDarkMode)} />
      </div>
    )
  }
  return (
    <Select<Action>
      items={ACTIONS}
      activeItem={selectedAction}
      itemRenderer={renderAction}
      onItemSelect={handleItemSelect}
      filterable={false}
      popoverProps={{ popoverClassName: "[&_.bp5-menu]:!max-h-[420px]" }}
    >
      <Button
        data-testid="stop-action-selector"
        minimal={true}
        disabled={disabled || isMobile}
      >
        <selectedAction.icon size={16} color={getIconColor(isDarkMode)} />
      </Button>
      {/*</Tooltip>*/}
    </Select>
  )
}

type RouteStopViewProps = {
  stop: RouteStop
  /** Already resolved against the rest of the route by the list. */
  displayedDistance: DisplayedStopDistance | undefined
  index: number
  /** Place among the stops still ahead (flag `map-stop-numbers`), as on the map */
  stopNumber?: number
  onUpdate: (stop: RouteStop, type: string) => void
  onRemove: (stop: RouteStop) => void
  onMove: (from: number, to: number) => void
  onMoveCompleted: () => void
  onDragCancelled?: () => void
  isUpdating: boolean
  isHighlighted?: boolean
  isReadOnlyMap?: boolean
  isDisabled?: boolean
  isEditing?: boolean
  onEditStart?: () => void
  onEditEnd?: () => void
  driverJid: string | undefined
  stationarySince?: string
  temporaryAvoidOptions?: AvoidType[]
  hideAvoid?: boolean
}

const RouteStopView = ({
  stop,
  displayedDistance,
  index,
  stopNumber,
  onUpdate,
  onRemove,
  onMove,
  onMoveCompleted,
  onDragCancelled,
  isUpdating,
  isHighlighted = false,
  isReadOnlyMap = false,
  isDisabled = false,
  isEditing = false,
  onEditStart,
  onEditEnd,
  driverJid,
  stationarySince,
  temporaryAvoidOptions,
  hideAvoid = false,
}: RouteStopViewProps) => {
  const { t } = useTranslation()
  const ref = React.useRef<HTMLTableRowElement>(null)
  const ItemType = "ROUTE_STOP"
  const isPassthrough = stop.action === "passthrough"
  const isArrived = stop.proximity_status === "arrived"

  const carRouteConfig = useAppSelector(findRouteConfig(driverJid || undefined))

  const automation = useStopAutomation(stop)

  const updateAvoidTolls = useCallback(
    (avoidTolls: boolean) => {
      onUpdate(withStopAvoidTolls(stop, avoidTolls), "avoid")
    },
    [stop, onUpdate],
  )

  const updateLocation = useCallback(
    (location: Location) => {
      onUpdate(withStopLocation(stop, location), "location")
    },
    [stop, onUpdate],
  )

  const getLocalISOString = (date: Date): string => {
    const offset = date.getTimezoneOffset() * 60000
    return new Date(date.getTime() - offset).toISOString().slice(0, -1)
  }

  const updateDate = useCallback(
    (date: Date | null) => {
      const updated = {
        ...stop,
        scheduled_arrival_time: date ? getLocalISOString(date) : undefined,
      }
      onUpdate(updated, "date")
    },
    [stop, onUpdate],
  )

  const updateAction = useCallback(
    (action: string) => {
      onUpdate(withStopAction(stop, action), "action")
    },
    [stop, onUpdate],
  )

  const [, drop] = useDrop({
    accept: ItemType,
    hover(item: { index: number; originalIndex: number }, monitor) {
      if (!ref.current || (!stop.is_active && !stop.is_temporary)) {
        return
      }

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      // Get rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Get mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = clientOffset?.y
        ? clientOffset.y - hoverBoundingRect.top
        : 0

      // Only perform the move when the mouse has crossed half of the item's height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex)

      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { index, originalIndex: index },
    canDrag: () =>
      (!!stop.is_active || !!stop.is_temporary) && !isUpdating && !isDisabled,
    end(item, monitor) {
      if (monitor.didDrop()) {
        // Only call onMoveCompleted if the item actually moved to a different position
        if (item.index !== item.originalIndex) {
          onMoveCompleted()
        }
      } else {
        // Drag was cancelled (dropped outside valid drop zone)
        // Restore original order
        if (item.index !== item.originalIndex && onDragCancelled) {
          onDragCancelled()
        }
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const Distance = () => {
    const distance = stopDistanceOf(stop, displayedDistance, {
      active: Boolean(stop.is_active),
      target: Boolean(stop.is_current_target),
      alert: Boolean(stop.shouldAlert),
    })
    if (!distance) return null

    return (
      <div className="flex items-center gap-1">
        <HoverPopover
          disabled={isMobile || !stop.proximity_status}
          content={<RouteStopDetailsPopover routeStop={stop} />}
          side="bottom"
        >
          <div>
            <Badge variant={distance.tone}>{distance.label}</Badge>
          </div>
        </HoverPopover>
        <StationaryIndicator
          proximityStatus={stop.proximity_status}
          stationarySince={stationarySince}
        />
      </div>
    )
  }

  const MenuButton = ({ hideAvoid = false }: { hideAvoid?: boolean }) => {
    const showCheck = stopAvoidsTolls(
      stop,
      temporaryAvoidOptions ?? carRouteConfig?.avoid,
    )

    return (
      <Popover2>
        <PopoverTrigger asChild>
          <Button
            minimal={true}
            disabled={isUpdating}
            aria-label={t("maps.routeStopView.openStopMenu")}
            className="invisible group-hover:visible dark:hover:bg-gray-700"
          >
            <MoreVertical
              size={16}
              color={isUpdating ? DISABLED_COLOR : "#5f6b7c"}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[1010] w-48 p-1" align="end">
          {automation.available && (
            <Button2
              variant="ghost"
              className="w-full justify-start text-left"
              onClick={() => automation.setOpen(true)}
            >
              <Bell className="mr-2 h-4 w-4" />
              {automation.pinnedCount > 0
                ? t("maps.routeStopView.automationWithCount", {
                    number: automation.pinnedCount,
                  })
                : t("maps.routeStopView.automation")}
            </Button2>
          )}
          {!hideAvoid && (
            <Button2
              variant="ghost"
              className="w-full justify-start text-left"
              onClick={() => updateAvoidTolls(!showCheck)}
            >
              {showCheck && <Check className="mr-2 h-4 w-4" />}
              {t("maps.routeStopView.avoidTolls")}
            </Button2>
          )}
          <Button2
            variant="ghost"
            className="w-full justify-start text-left text-red-500 hover:text-red-600"
            onClick={() => onRemove(stop)}
          >
            {t("maps.routeStopView.removeLocation")}
          </Button2>
        </PopoverContent>
      </Popover2>
    )
  }

  const textColor = useMemo(() => {
    if (!stop.isPending) {
      return "text-[#5F6B7C] dark:text-gray-500"
    }

    if (stop.shouldAlert) {
      return "text-[#CD4246] dark:text-red-400"
    }

    return "text-inherit dark:text-gray-200"
  }, [stop])

  const blinkingClass = isHighlighted ? css.blinking : ""
  const isSmallScreen = useMediaQuery("(max-width: 900px)")
  const isFromDriverLocation = stop.action === "from_driver_location"
  return (
    <tr
      data-testid="route-stop-item"
      id={`route-stop-${stop.id}`}
      ref={ref}
      style={{
        opacity: isDragging ? 0.5 : 1,
      }}
      className={`group ${textColor} ${blinkingClass} ${isDisabled ? "pointer-events-none opacity-50" : ""}`}
    >
      {!isMobile && !isSmallScreen && !isReadOnlyMap && (
        <td>
          {!isDisabled && (
            <Grip
              color="var(--grip-color, gray)"
              size={18}
              className={`invisible ${
                (stop.is_active || stop.is_temporary) &&
                !isFromDriverLocation &&
                "group-hover:visible"
              } cursor-move dark:text-gray-500`}
            />
          )}
        </td>
      )}
      <td className="relative">
        <ActionDisplay
          stop={stop}
          onActionUpdate={updateAction}
          isReadOnlyMap={isReadOnlyMap}
          disabled={
            isDisabled || (!stop.is_active && !stop.is_temporary) || isUpdating
          }
        />
        {stopNumber !== undefined && (
          <span
            data-testid="stop-number"
            className="pointer-events-none absolute right-0 top-0 h-4 min-w-4 rounded-full border border-white bg-[#1d4ed8] px-1 text-center text-[10px] font-bold leading-[14px] text-white"
          >
            {stopNumber}
          </span>
        )}
      </td>
      <td className="w-full">
        {automation.pinnedCount > 0 && (
          // The row is dense already (distance, ETA, status), so this stays an
          // icon plus a count — enough to stop dispatchers stacking duplicates.
          <Badge
            variant="outline"
            data-testid="stop-automation-badge"
            title={t("maps.routeStopView.automationsSet", {
              count: automation.pinnedCount,
            })}
            className="mr-1 gap-1 px-1 py-0 align-middle text-[10px]"
          >
            <Bell className="h-3 w-3" />
            {automation.pinnedCount}
          </Badge>
        )}
        <LocationView
          stop={stop}
          onUpdate={updateLocation}
          disabled={
            isDisabled ||
            (!stop.is_active && !stop.is_temporary) ||
            isUpdating ||
            isFromDriverLocation
          }
          detected={ChatHelpers.isMessageFromSlickAI(stop.by || "")}
          isEditing={isEditing}
          onEditStart={onEditStart}
          onEditEnd={onEditEnd}
          isReadOnlyMap={isReadOnlyMap}
        />
      </td>
      <td className="whitespace-nowrap text-right" id="stop-distance">
        <Distance />
      </td>
      {!isMobile && !isSmallScreen && !isReadOnlyMap && (
        <td>
          {stop.is_active && !isPassthrough && !isDisabled && (
            <ScheduleButton
              stop={stop}
              onDateUpdate={updateDate}
              disabled={isUpdating}
            />
          )}
        </td>
      )}
      <td className="whitespace-nowrap text-right" id="stop-remaining-time">
        <Delay stop={stop} />
      </td>
      {!isMobile && !isSmallScreen && !isReadOnlyMap && (
        <td>
          {!isDisabled && (
            <MenuButton hideAvoid={hideAvoid || (!driverJid && index === 0)} />
          )}
          {automation.available && (
            <StopAutomationDialog
              open={automation.isOpen}
              onOpenChange={automation.setOpen}
              stopId={stop.id}
              stopAction={stop.action}
              stopLabel={automation.label}
              targetJid={driverJid}
            />
          )}
        </td>
      )}
      {(isMobile || isSmallScreen) && !isReadOnlyMap && (
        <td className="pl-2">
          {(stop.is_active || stop.is_temporary) && !isDisabled && (
            <Button
              minimal={true}
              disabled={isUpdating}
              onClick={() => onRemove(stop)}
              className="dark:hover:bg-gray-700"
            >
              <Trash2 size={16} color="#CD4246" />
            </Button>
          )}
        </td>
      )}
    </tr>
  )
}

export default RouteStopView
